import {NfcRecordType, NfcScanResult, NfcValue} from './types';

export interface NfcRecord {
    recordType: string;
    encoding?: string;
    data?: DataView | null;
}

export interface NfcReadingEvent extends Event {
    serialNumber: string;
    message: {records: readonly NfcRecord[]};
}

export interface NfcReader extends EventTarget {
    scan: (options: {signal: AbortSignal}) => Promise<void>;
    write: (
        message: {records: {recordType: NfcRecordType; data: string}[]},
        options: {signal: AbortSignal; overwrite: boolean},
    ) => Promise<void>;
}

type NfcWindow = Window & {NDEFReader?: new () => NfcReader};

export interface NfcSession {
    reader: NfcReader;
    signal: AbortSignal;
    addCleanup: (cleanup: () => void) => void;
    close: () => void;
}

let activeSession: NfcSession | null = null;

export const getNfcUnavailableMessage = (): string | undefined => {
    if (typeof window === 'undefined' || !window.isSecureContext) {
        return 'NFC werkt alleen via een beveiligde verbinding (HTTPS).';
    }
    if (!(window as NfcWindow).NDEFReader) {
        return 'Deze browser ondersteunt geen NFC. Gebruik Chrome op een Android-telefoon met NFC.';
    }
};

export const openNfcSession = (): NfcSession => {
    const unavailable = getNfcUnavailableMessage();
    if (unavailable) throw new Error(unavailable);
    if (activeSession) throw new Error('Er is al een NFC-actie bezig. Stop die eerst en probeer het opnieuw.');

    const Reader = (window as NfcWindow).NDEFReader!;
    const reader = new Reader();
    const controller = new AbortController();
    const cleanups: (() => void)[] = [];
    const session: NfcSession = {
        reader,
        signal: controller.signal,
        addCleanup: (cleanup) => cleanups.push(cleanup),
        close: () => {
            if (activeSession !== session) return;
            activeSession = null;
            cleanups.forEach((cleanup) => cleanup());
            controller.abort();
        },
    };
    activeSession = session;
    return session;
};

export const decodeNfcReading = (event: NfcReadingEvent): NfcScanResult => {
    const records: NfcValue[] = [];
    event.message.records.forEach((record) => {
        if (record.recordType !== 'text' && record.recordType !== 'url') return;
        const encoding = record.recordType === 'text' ? record.encoding || 'utf-8' : 'utf-8';
        records.push({
            type: record.recordType,
            value: new TextDecoder(encoding).decode(record.data ?? undefined),
        });
    });
    return {serialNumber: event.serialNumber || '', records};
};

export const scanNfcTag = (
    session: NfcSession,
    {allowEmptyRecords = false} = {},
): Promise<NfcScanResult> => new Promise((resolve, reject) => {
    const onReading: EventListener = (event) => {
        detach();
        try {
            const result = decodeNfcReading(event as NfcReadingEvent);
            if (!allowEmptyRecords && !result.records.length) throw new Error();
            resolve(result);
        } catch {
            reject(new Error('Deze tag bevat geen leesbare tekst of URL. Probeer een andere NFC-tag.'));
        }
    };
    const onReadingError = () => {
        detach();
        reject(new Error('De tag kon niet worden gelezen. Houd hem dichterbij en probeer opnieuw.'));
    };
    const detach = () => {
        session.reader.removeEventListener('reading', onReading);
        session.reader.removeEventListener('readingerror', onReadingError);
    };
    session.reader.addEventListener('reading', onReading);
    session.reader.addEventListener('readingerror', onReadingError);
    session.addCleanup(() => {
        detach();
        reject(new DOMException('NFC geannuleerd', 'AbortError'));
    });
    session.reader.scan({signal: session.signal}).catch(reject);
});

export const writeNfcTag = async (session: NfcSession, payload: NfcValue): Promise<NfcValue> => {
    await session.reader.write(
        {records: [{recordType: payload.type, data: payload.value}]},
        {signal: session.signal, overwrite: true},
    );
    return payload;
};

export const getNfcWriteValidation = ({type, value}: NfcValue): string | undefined => {
    if (!value.trim()) return 'Geef eerst tekst of een URL op om te schrijven.';
    if (type !== 'url') return;
    try {
        new URL(value);
    } catch {
        return 'Geef een volledige URL op, bijvoorbeeld https://voorbeeld.nl.';
    }
};

const errorMessages: Record<string, string> = {
    NotAllowedError: 'Geen toestemming voor NFC. Sta NFC-toegang toe in je browser en probeer opnieuw.',
    SecurityError: 'NFC-toegang is geblokkeerd. Open deze pagina rechtstreeks via HTTPS en probeer opnieuw.',
    NotSupportedError: 'NFC is niet beschikbaar. Controleer of je telefoon NFC ondersteunt en of NFC aanstaat.',
    NotFoundError: 'Geen NFC-apparaat gevonden. Controleer of NFC op je telefoon aanstaat.',
    NotReadableError: 'NFC is niet beschikbaar. Zet NFC aan en houd een geschikte tag bij je telefoon.',
    NetworkError: 'De tag kon niet worden gelezen of beschreven. Houd hem stil en controleer of hij beschrijfbaar is.',
    TypeError: 'Deze inhoud kan niet naar de chip worden geschreven. Controleer de tekst en gebruik een geschikte NDEF-chip.',
    DataError: 'De inhoud kan niet op deze chip worden opgeslagen. Controleer of de chip voldoende ruimte heeft.',
    InvalidStateError: 'NFC kan nu niet starten. Houd deze pagina open en probeer opnieuw.',
    AbortError: 'De NFC-actie is onderbroken. Probeer het opnieuw.',
};

export const toNfcError = (cause: unknown): Error => {
    if (!(cause instanceof Error)) return new Error('De NFC-actie is mislukt. Probeer het opnieuw.');
    const message = errorMessages[cause.name];
    if (!message) return cause;
    const error = new Error(message);
    error.name = cause.name;
    return error;
};
